import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Star, Shield, Target, Play } from 'lucide-react';
import './casestudy.css';
import { Link } from 'react-router-dom';

// Register GSAP plugins
gsap.registerPlugin(ScrollTrigger);

const CaseStudy = () => {
  const sectionRef = useRef(null);
  const orbRefs = useRef([]);
  const cardRefs = useRef([]);
  const badgeRef = useRef(null);
  const titleRef = useRef(null);

  useEffect(() => {
    const section = sectionRef.current;
    
    // Orb floating animations
    gsap.to(orbRefs.current[0], {
      y: -30,
      x: 20,
      duration: 8,
      ease: "power2.inOut",
      yoyo: true,
      repeat: -1
    });

    gsap.to(orbRefs.current[1], {
      y: 25,
      x: -15,
      duration: 6,
      ease: "power2.inOut",
      yoyo: true,
      repeat: -1
    });

    // Badge shimmer animation
    gsap.fromTo(badgeRef.current, 
      { 
        opacity: 0,
        y: 30,
        scale: 0.9
      },
      {
        opacity: 1,
        y: 0,
        scale: 1,
        duration: 1,
        ease: "back.out(1.7)",
        scrollTrigger: {
          trigger: badgeRef.current,
          start: "top 90%",
          toggleActions: "play none none reverse"
        }
      }
    );

    // Title reveal animation
    gsap.fromTo(titleRef.current,
      {
        opacity: 0,
        y: 50
      },
      {
        opacity: 1,
        y: 0,
        duration: 1.2,
        ease: "power3.out",
        scrollTrigger: {
          trigger: titleRef.current,
          start: "top 85%",
          toggleActions: "play none none reverse"
        }
      }
    );

    // Animate cards on scroll
    cardRefs.current.forEach((card, index) => {
      if (card) {
        gsap.fromTo(card,
          {
            opacity: 0,
            y: 60,
            scale: 0.9
          },
          {
            opacity: 1,
            y: 0,
            scale: 1,
            duration: 0.8,
            delay: index * 0.1,
            ease: "back.out(1.7)",
            scrollTrigger: {
              trigger: card,
              start: "top 90%",
              toggleActions: "play none none reverse"
            }
          }
        );
      }
    });

    // Parallax effect for background elements
    gsap.to('.floating-particles', {
      y: -50,
      ease: "none",
      scrollTrigger: {
        trigger: section,
        start: "top bottom",
        end: "bottom top",
        scrub: 1
      }
    });

    return () => {
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, []);

  const addToRefs = (el, refsArray) => {
    if (el && !refsArray.current.includes(el)) {
      refsArray.current.push(el);
    }
  };

  return (
    <section className="next-section" ref={sectionRef}>
      <div className="section-bg">
        <div className="orb-4" ref={el => orbRefs.current[0] = el}></div>
        <div className="orb-5" ref={el => orbRefs.current[1] = el}></div>
        <div className="floating-particles"></div>
      </div>

      <div className="container">
        {/* Header Section */}
        <div className="content-block text-center">
          <div className="section-badge" ref={badgeRef}>
            <svg className="badge-icon" fill="currentColor" viewBox="0 0 24 24">
              <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
            </svg>
            BEYOND THE ANALYSIS
          </div>
          <h1 className="section-title" ref={titleRef}>
            Data-Driven <span className="highlight">Performance Marketing</span> Excellence
          </h1>
          <p className="section-description">
            Beyond the Analysis (BTA) specializes in data-driven, full-funnel performance marketing for e-commerce brands. 
            Their core values are integrity and transparency, which they believe ensure long-term client success.
          </p>
        </div>

        {/* Client Portfolio */}
        {/* Client Portfolio */}
<div className="content-block text-center">
  <h2 className="section-title" style={{fontSize: '2.5rem', marginBottom: '3rem'}}>
    Trusted by <span className="highlight">Industry Leaders</span>
  </h2>
  <div className="platforms-grid">
    {[
      {
        name: "",
        logo: "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQiK8326P-vEYZsB_HOwkB1dXnorkFal_bBBQ&s"
      },
      {
        name: "",
        logo: "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcRl92CxGH4u8ebcUPGOATZoG0Gj6T4lfzDA1w&s"
      }
   
    
    ].map((client, index) => (
      <div 
        key={client.name}
        className="platform-card"
        ref={el => addToRefs(el, cardRefs)}
      >
        <div className="platform-logo-wrapper">
          <img 
            src={client.logo} 
            alt={client.name} 
            className="platform-logo"
          />
          <div className="platform-name">{client.name}</div>
        </div>
        <div className="platform-chart">
          <div className="chart-bar" style={{'--final-height': `${60 + index * 10}%`}}></div>
        </div>
      </div>
    ))}
  </div>
</div>


        {/* Services Framework */}
        <div className="framework-section">
          <div className="framework-header text-center">
            <h2 className="section-title">Our <span className="highlight">Service Framework</span></h2>
            <p className="section-description" style={{textAlign: 'center'}}>
              Beyond the Analysis offers comprehensive services across three key marketing channels
            </p>
          </div>
          
          <div className="framework-content">
            <div className="framework-pillar" ref={el => addToRefs(el, cardRefs)}>
              <div className="pillar-header">
                <svg className="pillar-icon" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
                </svg>
                <h3>Paid Search</h3>
              </div>
              <p>Google Ads and Bing Ads optimization with advanced keyword strategies and competitive analysis</p>
            </div>
            
            <div className="framework-pillar" ref={el => addToRefs(el, cardRefs)}>
              <div className="pillar-header">
                <svg className="pillar-icon" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                </svg>
                <h3>Paid Social</h3>
              </div>
              <p>Meta Ads (Facebook and Instagram) and TikTok Ads with creative optimization and audience targeting</p>
            </div>
            
            <div className="framework-pillar" ref={el => addToRefs(el, cardRefs)}>
              <div className="pillar-header">
                <svg className="pillar-icon" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M7 18c0 1.1.9 2 2 2s2-.9 2-2-.9-2-2-2-2 .9-2 2zM1 2v2h2l3.6 7.59-1.35 2.45c-.16.28-.25.61-.25.96 0 1.1.9 2 2 2h12v-2H7.42c-.14 0-.25-.11-.25-.25l.03-.12L8.1 13h7.45c.75 0 1.41-.41 1.75-1.03L21.7 4H5.21l-.94-2H1zm16 16c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"/>
                </svg>
                <h3>Paid Marketplace</h3>
              </div>
              <p>Amazon Ads, Walmart Ads, and eBay Ads with marketplace-specific optimization strategies</p>
            </div>
          </div>
        </div>

        {/* Case Studies */}
        <div className="content-block text-center">
          <h2 className="section-title">
            <span className="highlight">Case Studies</span> That Drive Results
          </h2>
          
          {/* UBLOCKOUT Case Study */}
          <div className="feature-card" ref={el => addToRefs(el, cardRefs)} style={{marginBottom: '4rem'}}>
            <div className="feature-icon">
              <svg fill="currentColor" viewBox="0 0 24 24">
                <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/>
              </svg>
            </div>
            <h3 className="feature-title">UBLOCKOUT.COM - Blackout Shade Innovation</h3>
            <p className="feature-description" style={{marginBottom: '2rem'}}>
              UBlockout, based in Dallas, offers blackout shades for better sleep. After success in Australia with "ScreenAway," 
              they needed help scaling in the US market.
            </p>
            
            <div className="strategy-points">
              <div className="strategy-point">
                <div className="point-icon">
                  <svg fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                  </svg>
                </div>
                <div className="point-content">
                  <h4>The Challenge</h4>
                  <p>Limited US market knowledge, low MER (1.5), declining sales, high CPA, and ineffective one-dimensional strategy</p>
                </div>
              </div>
              
              <div className="strategy-point">
                <div className="point-icon">
                  <svg fill="currentColor" viewBox="0 0 24 24">
                    <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                </div>
                <div className="point-content">
                  <h4>Our Solution</h4>
                  <p>Complete account audit, US market research, multi-funnel approach with diversified Google strategy (Search, Shopping, Pmax), targeting adjustment, and creative development</p>
                </div>
              </div>
              
              <div className="strategy-point">
                <div className="point-icon">
                  <svg fill="currentColor" viewBox="0 0 24 24">
                    <path d="M13 2.05v2.02c4.39.54 7.5 4.53 6.96 8.92-.39 3.18-2.79 5.58-5.96 5.96v2.02c5.5-.55 9.5-5.43 8.95-10.93C22.45 6.04 18.05 2.5 13 2.05z"/>
                  </svg>
                </div>
                <div className="point-content">
                  <h4>Results Achieved</h4>
                  <p>Sales tripled from $150K to $450K with same $100K ad spend. MER improved from 1.5 to 4.5 in three months</p>
                </div>
              </div>
            </div>
          </div>

          {/* TREASURE4NAILS Case Study */}
          <div className="feature-card" ref={el => addToRefs(el, cardRefs)} style={{marginBottom: '4rem'}}>
            <div className="feature-icon">
              <svg fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
              </svg>
            </div>
            <h3 className="feature-title">TREASURE4NAILS - Beauty Products Revolution</h3>
            <p className="feature-description" style={{marginBottom: '2rem'}}>
              A nail beauty products store with over 1000 professional products from top brands, 
              facing declining sales and tracking issues.
            </p>
            
            <div className="strategy-points">
              <div className="strategy-point">
                <div className="point-icon">
                  <svg fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                  </svg>
                </div>
                <div className="point-content">
                  <h4>The Challenge</h4>
                  <p>Sales decline, conversion tracking issues, focus on vanity metrics, ineffective ad strategy, and poor traffic quality</p>
                </div>
              </div>
              
              <div className="strategy-point">
                <div className="point-icon">
                  <svg fill="currentColor" viewBox="0 0 24 24">
                    <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                </div>
                <div className="point-content">
                  <h4>Our Strategy</h4>
                  <p>New customer focus, integrated Meta and Google campaigns, product-focused promotion strategy, and restructured campaigns</p>
                </div>
              </div>
              
              <div className="strategy-point">
                <div className="point-icon">
                  <svg fill="currentColor" viewBox="0 0 24 24">
                    <path d="M13 2.05v2.02c4.39.54 7.5 4.53 6.96 8.92-.39 3.18-2.79 5.58-5.96 5.96v2.02c5.5-.55 9.5-5.43 8.95-10.93C22.45 6.04 18.05 2.5 13 2.05z"/>
                  </svg>
                </div>
                <div className="point-content">
                  <h4>Outstanding Results</h4>
                  <p>Reduced ad spend from $14K to $9K while doubling sales from $45K to $90K. MER improved from 3.2 to 10.0</p>
                </div>
              </div>
            </div>
          </div>
          
          {/* New Case Studies */}
          {/* <div className="feature-card" ref={el => addToRefs(el, cardRefs)} style={{marginBottom: '4rem'}}>
            <div className="feature-icon" style={{backgroundColor: '#86B94A'}}>
              <Star color="#000" />
            </div>
            <h3 className="feature-title">Nutriganix - Performance Marketing</h3>
            <p className="feature-description" style={{marginBottom: '1rem'}}>
              Operating from within the United States, Nutriganix is dedicated to providing safe and cost-effective herbal supplements for global consumers.
            </p>
            <div className="strategy-points">
              <div className="strategy-point">
                <div className="point-icon" style={{backgroundColor: '#86B94A'}}>
                  <Target color="#000" />
                </div>
                <div className="point-content">
                  <h4>The Challenge</h4>
                  <p>Shopping Ads were blocked due to GMC integration issues with their CMS, and inaccurate conversion tracking resulted in unreliable performance data.</p>
                </div>
              </div>
              <div className="strategy-point">
                <div className="point-icon" style={{backgroundColor: '#86B94A'}}>
                  <Play color="#000" />
                </div>
                <div className="point-content">
                  <h4>The Solution</h4>
                  <p>The Solutions 8 team conducted weekly client meetings, rectified conversion tracking, successfully integrated GMC with UltraCart to unlock Shopping Ads, and launched optimized Performance Max campaigns.</p>
                </div>
              </div>
              <div className="strategy-point">
                <div className="point-icon" style={{backgroundColor: '#86B94A'}}>
                  <Star color="#000" />
                </div>
                <div className="point-content">
                  <h4>The Results</h4>
                  <p>Within just 4 months, Nutriganix achieved a total generated revenue of $145,556.27 from an ad spend of $31,386.01, resulting in a Media Efficiency Ratio (MER) of 4.64.</p>
                </div>
              </div>
            </div>
          </div> */}
          
          {/* <div className="feature-card" ref={el => addToRefs(el, cardRefs)}>
            <div className="feature-icon" style={{backgroundColor: '#6E8B62'}}>
              <Shield color="#000" />
            </div>
            <h3 className="feature-title">Four Leaf Rover - Pet Supplements</h3>
            <p className="feature-description" style={{marginBottom: '1rem'}}>
              Four Leaf Rover is a pet supplement manufacturer and distributor, a trusted brand in the pet wellness industry known for premium, safe, and natural supplements.
            </p>
            <div className="strategy-points">
              <div className="strategy-point">
                <div className="point-icon" style={{backgroundColor: '#6E8B62'}}>
                  <Target color="#000" />
                </div>
                <div className="point-content">
                  <h4>The Challenge</h4>
                  <p>After iOS privacy changes, their previously successful Facebook Ads performance declined. They needed to scale their business using Google Ads to increase sales, lower CPC, and grow revenue.</p>
                </div>
              </div>
              <div className="strategy-point">
                <div className="point-icon" style={{backgroundColor: '#6E8B62'}}>
                  <Play color="#000" />
                </div>
                <div className="point-content">
                  <h4>The Solution</h4>
                  <p>Our team designed and executed a comprehensive Google Ads strategy by building a solid campaign structure, closely monitoring performance, selecting high-impact campaigns (Shopping, Dynamic Remarketing, Branded), and implementing accurate conversion tracking.</p>
                </div>
              </div>
              <div className="strategy-point">
                <div className="point-icon" style={{backgroundColor: '#6E8B62'}}>
                  <Star color="#000" />
                </div>
                <div className="point-content">
                  <h4>The Results</h4>
                  <p>In 18 months, they achieved a 4.5X increase in revenue, a 153% increase in ROAS, and a 32% decrease in CPC, proving Google Ads could be a sustainable and scalable growth channel for their business.</p>
                </div>
              </div>
            </div>
          </div> */}
        </div>

        {/* Benefits Section */}
        <div className="benefits-list text-center">
          <h2 className="benefits-title">Why Choose Beyond the Analysis</h2>
          <div className="benefits-grid">
            {[
              'Data-driven decision making',
              'Full-funnel optimization',
              'Transparent reporting',
              'Multi-platform expertise',
              'ROI-focused strategies',
              'Continuous optimization'
            ].map((benefit, index) => (
              <div 
                key={benefit}
                className="benefit-item"
                ref={el => addToRefs(el, cardRefs)}
              >
                <svg className="check-icon" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
                <span>{benefit}</span>
              </div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <div className="cta-section text-center">
          <Link to="/consultation">
          <button className="cta-button">
            Start Your Success Story
            <svg className="cta-icon" fill="currentColor" viewBox="0 0 24 24">
              <path d="M5 12h14m-7-7l7 7-7 7"/>
            </svg>
          </button>
          </Link>
          <p className="cta-note">Ready to scale your e-commerce business? Let's talk.</p>
        </div>
      </div>
    </section>
  );
};

export default CaseStudy;
